import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import { StylesConfig } from "react-select";

export interface Option {
  value: string;
  label: string;
}

interface MultiSelectProps {
  initialValue?: string[];
  onSelectionChange: (values: string[]) => void;
  options: Option[];
  placeholder?: string;
  className?: string;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  initialValue = [],
  onSelectionChange,
  options,
  placeholder = "Select...",
  className = ""
}) => {
  const [selectedValues, setSelectedValues] = useState<Option[]>([]);
  const [inputValue, setInputValue] = useState("");
  
  const isMounted = useRef(true);
  
  useEffect(() => {
    isMounted.current = true;
    
    return () => {
      isMounted.current = false;
    };
  }, []);
  
  useEffect(() => {
    if (!isMounted.current) return;
    
    if (initialValue && initialValue.length > 0 && options.length > 0) {
      const matchingOptions = options.filter(option => 
        initialValue.includes(option.value)
      );
      
      if (JSON.stringify(matchingOptions) !== JSON.stringify(selectedValues)) {
        setSelectedValues(matchingOptions);
      }
    } else if (initialValue && initialValue.length === 0 && selectedValues.length > 0) {
      setSelectedValues([]);
    }
  }, [initialValue, options, selectedValues]);
  
  const handleInputChange = (newValue: string, { action }: any) => {
    if (action === "input-change") {
      setInputValue(newValue);
    }
  };
  
  const handleChange = (selected: readonly Option[]) => {
    if (!isMounted.current) return;
    
    const selectedOpts = selected as Option[];
    setSelectedValues(selectedOpts);
    
    const values = selectedOpts.map(option => option.value);
    onSelectionChange(values);
  };
  
  const customStyles: StylesConfig<Option, true> = {
    control: (base) => ({
      ...base,
      minHeight: 60,
    }),
    menu: (base) => ({
      ...base,
      minWidth: 250,
    }),
    multiValue: (base) => ({
      ...base,
      maxWidth: 'none',
      display: 'flex',
    }),
    multiValueLabel: (base) => ({
      ...base,
      whiteSpace: 'normal',
      overflow: 'visible',
      textOverflow: 'initial',
      paddingRight: '6px',
    }),
    valueContainer: (base) => ({
      ...base,
      flexWrap: 'wrap',
    }),
    placeholder: (base) => ({
      ...base,
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'italic',
      color: '#D9D7D4',
    }),
  };
  
  return (
    <div className={className}>
      <Select
        options={options}
        isMulti
        closeMenuOnSelect={false}
        onChange={handleChange}
        value={selectedValues}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        placeholder={placeholder}
        styles={customStyles}
        loadingMessage={() => "Loading..."}
      />
    </div>
  );
};

export default MultiSelect;